import { useEffect } from "react";
import {
  HeadContent,
  Outlet,
  Scripts,
  createRootRouteWithContext,
  useRouter,
} from "@tanstack/react-router";
import { QueryClientProvider, type QueryClient } from "@tanstack/react-query";
import { HelmetProvider } from "@/lib/helmet-compat";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AuthProvider } from "@/hooks/useAuth";
import { CodeInjection } from "@/components/layout/CodeInjection";
import { ScrollToTop } from "@/components/layout/ScrollToTop";
import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import NotFound from "@/pages/NotFound";
import { reportLovableError } from "@/lib/lovable-error-reporting";
import appCss from "../styles.css?url";

const GA_MEASUREMENT_ID = "G-7H9TPW2HKE";
const AHREFS_ANALYTICS_KEY = "5hSLrJtc+RMgx+iHR+E6sg";

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()({
  head: () => ({
    meta: [
      { charSet: "utf-8" },
      { name: "viewport", content: "width=device-width, initial-scale=1.0" },
      { title: "Kodiak Structures & Exteriors | Roofing & Siding Contractor" },
      {
        name: "description",
        content:
          "Expert roofing, siding, window and gutter services built to withstand the toughest Minnesota weather. Serving homeowners throughout Minnesota.",
      },
      {
        name: "keywords",
        content:
          "roofing, siding, windows, storm damage, gutters, roofing contractor, Blaine, Plymouth, Woodbury, St. Paul, Twin Cities, Minnesota",
      },
      { name: "author", content: "Kodiak Structures & Exteriors" },
      { property: "og:type", content: "website" },
      { property: "og:site_name", content: "Kodiak Structures & Exteriors" },
      { property: "og:url", content: "https://kodiakse.com" },
      {
        property: "og:title",
        content: "Kodiak Structures & Exteriors | Roofing & Siding Contractor",
      },
      {
        property: "og:description",
        content:
          "Kodiak Structures & Exteriors delivers expert roofing, siding, windows, and gutter services built to withstand the toughest conditions. Serving homeowners throughout Minnesota.",
      },
      { property: "og:image", content: "https://kodiakse.com/og-image.png" },
      { name: "twitter:card", content: "summary_large_image" },
      {
        name: "twitter:title",
        content: "Kodiak Structures & Exteriors | Roofing & Siding Contractor",
      },
      {
        name: "twitter:description",
        content:
          "Kodiak Structures & Exteriors delivers expert roofing, siding, windows, and gutter services built to withstand the toughest conditions. Serving homeowners throughout Minnesota.",
      },
      { name: "twitter:image", content: "https://kodiakse.com/og-image.png" },
    ],
    links: [
      { rel: "stylesheet", href: appCss },
      // favicon.svg was B Rico Mechanical's logo, left over from the remix this
      // project was built from. These are generated from the Kodiak bear mark.
      { rel: "icon", href: "/favicon.ico", sizes: "any" },
      { rel: "icon", type: "image/png", sizes: "32x32", href: "/favicon-32x32.png" },
      { rel: "icon", type: "image/png", sizes: "16x16", href: "/favicon-16x16.png" },
      { rel: "apple-touch-icon", sizes: "180x180", href: "/apple-touch-icon.png" },
      { rel: "preconnect", href: "https://fonts.googleapis.com" },
      { rel: "preconnect", href: "https://fonts.gstatic.com", crossOrigin: "anonymous" },
      {
        rel: "preload",
        href: "https://fonts.googleapis.com/css2?family=Oswald:wght@400;700&display=swap",
        as: "style",
      },
      {
        rel: "stylesheet",
        href: "https://fonts.googleapis.com/css2?family=Oswald:wght@400;700&family=Barlow:wght@400;500;600;700&family=Poppins:wght@300;400;500;700&display=swap",
      },
    ],
    // Google Analytics 4. Lives here rather than in the admin code-injection
    // settings so it is server-rendered into every page's head on first paint.
    scripts: [
      // Skill Mammoth portal analytics (Website Traffic panel), cookie-free
      { src: "https://newportal.skillmammoth.com/sm.js", "data-site": "1362219476b1461c", defer: true },
      {
        src: `https://www.googletagmanager.com/gtag/js?id=${GA_MEASUREMENT_ID}`,
        async: true,
      },
      // Ahrefs Web Analytics. The project (Kodiakse, 10286582) was already set
      // up and verified but had no tag on the site, which is why it reported
      // zero visitors.
      {
        src: "https://analytics.ahrefs.com/analytics.js",
        "data-key": AHREFS_ANALYTICS_KEY,
        async: true,
      },
      {
        children: `window.dataLayer = window.dataLayer || [];
function gtag(){dataLayer.push(arguments);}
gtag('js', new Date());
gtag('config', '${GA_MEASUREMENT_ID}');`,
      },
    ],
  }),
  shellComponent: RootShell,
  component: RootComponent,
  notFoundComponent: NotFoundComponent,
  errorComponent: ErrorComponent,
});

function RootShell({ children }: { children: React.ReactNode }) {
  return (
    <html lang="en" suppressHydrationWarning>
      <head>
        <HeadContent />
      </head>
      <body>
        {children}
        <Scripts />
      </body>
    </html>
  );
}

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  return (
    <HelmetProvider>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <TooltipProvider>
            <Toaster />
            <Sonner />
            <CodeInjection />
            <ScrollToTop />
            <Outlet />
          </TooltipProvider>
        </AuthProvider>
      </QueryClientProvider>
    </HelmetProvider>
  );
}

function NotFoundComponent() {
  return (
    <div className="flex flex-col min-h-screen">
      <Navbar />
      <div className="flex-1">
        <NotFound />
      </div>
      <Footer />
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  useEffect(() => {
    reportLovableError(error, { boundary: "tanstack_root_error_component" });
  }, [error]);
  return (
    <div className="min-h-screen flex items-center justify-center bg-background px-6">
      <div className="max-w-md w-full text-center space-y-4">
        <h1 className="text-2xl font-heading font-bold text-foreground">
          This page didn't load
        </h1>
        <p className="text-muted-foreground">
          Something went wrong on our end. You can try again or head back home.
        </p>
        <div className="flex items-center justify-center gap-3">
          <button
            className="px-4 py-2 rounded-md bg-primary text-primary-foreground"
            onClick={() => {
              router.invalidate();
              reset();
            }}
          >
            Try again
          </button>
          <a
            className="px-4 py-2 rounded-md border border-border text-foreground"
            href="/"
          >
            Go home
          </a>
        </div>
      </div>
    </div>
  );
}
